import React, { useMemo } from 'react';

import { Card } from '@/components/ui/card';
import { useRequiredParams } from 'utils';
import { useGlobalAccountFeeRuleTimeline, useGlobalAccountSettlementRuleTimeline } from 'views/billing/billing-queries';
import { PricingOverridesPanel } from 'views/billing/PricingOverridesPanel';
import { PricingProfilePanel } from 'views/billing/PricingProfilePanel';
import { SettlementOverridesPanel } from 'views/billing/SettlementOverridesPanel';
import { SettlementProfilePanel } from 'views/billing/SettlementProfilePanel';

const explanation =
    'Please configure one that starts before the next billing cycle or this global account will not be billed.';

export const BillingTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    const { data: feeRuleTimeline, isPending: feeRuleTimelineLoading } =
        useGlobalAccountFeeRuleTimeline(globalAccountGUID);
    const { data: settlementRuleTimeline, isPending: settlementRuleTimelineLoading } =
        useGlobalAccountSettlementRuleTimeline(globalAccountGUID);

    const warning = useMemo(() => {
        if (feeRuleTimelineLoading || settlementRuleTimelineLoading) {
            return null;
        }

        // if both of them have an active rule or neither of them has anything set up, we're good
        if (
            (feeRuleTimeline?.activeRules.length && settlementRuleTimeline?.activeRules.length) ||
            (!feeRuleTimeline?.activeRules.length &&
                !settlementRuleTimeline?.activeRules.length &&
                !feeRuleTimeline?.upcomingRules.length &&
                !settlementRuleTimeline?.upcomingRules.length)
        ) {
            return null;
        }

        // check existence of active rules
        if (feeRuleTimeline?.activeRules.length && !settlementRuleTimeline?.activeRules.length) {
            return `Missing active settlement profile. ${explanation}`;
        }
        if (settlementRuleTimeline?.activeRules.length && !feeRuleTimeline?.activeRules.length) {
            return `Missing active pricing profile. ${explanation}`;
        }

        // check existence of upcoming rules
        if (feeRuleTimeline?.upcomingRules.length && !settlementRuleTimeline?.upcomingRules.length) {
            return `Missing upcoming settlement profile. ${explanation}`;
        }
        if (settlementRuleTimeline?.upcomingRules.length && !feeRuleTimeline?.upcomingRules.length) {
            return `Missing upcoming pricing profile. ${explanation}`;
        }

        // check if upcoming rules have matching dates
        if (feeRuleTimeline?.upcomingRules[0].startDate !== settlementRuleTimeline?.upcomingRules[0].startDate) {
            return 'Upcoming pricing profile start date and settlement profile start date are mismatched.';
        }
    }, [
        feeRuleTimeline?.activeRules,
        feeRuleTimeline?.upcomingRules,
        feeRuleTimelineLoading,
        settlementRuleTimeline?.activeRules,
        settlementRuleTimeline?.upcomingRules,
        settlementRuleTimelineLoading,
    ]);

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-cocoa-1000 text-xl">Billing Setup</h2>
            {warning && (
                <div className="rounded-md border-l-4 border-yellow-400 bg-yellow-50 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">{warning}</p>
                        </div>
                    </div>
                </div>
            )}

            <Card className="flex">
                <div className="border-border flex-1 border-r p-8 last:border-r-0">
                    <PricingProfilePanel />
                </div>
                <div className="border-border flex-1 border-r p-8 last:border-r-0">
                    <SettlementProfilePanel />
                </div>
            </Card>
            <Card className="flex">
                <div className="border-border flex-1 border-r p-8 last:border-r-0">
                    <PricingOverridesPanel />
                </div>
                <div className="border-border flex-1 border-r p-8 last:border-r-0">
                    <SettlementOverridesPanel />
                </div>
            </Card>
        </div>
    );
};
